/*! *********************************************************************
 *
 * Copyright 2019 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *************************************************************************
 */

import gql from 'graphql-tag';

export const ASSET_FRAGMENT = gql`
  fragment AssetFields on Asset {
    id
    attachments {
      id
      tags
      label
      ... on PreviewAttachment {
        url
        kind
      }
      ... on DownloadAttachment {
        url
        filename
        mimetype
        size
        revisions {
          filename
          mimetype
          size
          revision
        }
      }
    }
  }
`;

export const SET_THUMBNAIL = gql`
  mutation SetThumbnail(
    $extraTags: [String!] = []
    $fileId: String!
    $assetId: String!
  ) {
    setThumbnail(extraTags: $extraTags, fileId: $fileId, assetId: $assetId) {
      ...AssetFields
    }
  }

  ${ASSET_FRAGMENT}
`;

export const ADD_PREVIEW_ATTACHMENT = gql`
  mutation AddPreviewAttachment(
    $label: String = "preview"
    $extraTags: [String!] = []
    $fileId: String!
    $assetId: String!
  ) {
    addPreviewAttachment(
      label: $label
      extraTags: $extraTags
      fileId: $fileId
      assetId: $assetId
    ) {
      ...AssetFields
    }
  }

  ${ASSET_FRAGMENT}
`;

export const UPDATE_ASSET_FILE = gql`
  mutation UpdateAssetFile(
    $isNewVersion: Boolean
    $fileId: String!
    $id: String!
  ) {
    updateAssetFile(isNewVersion: $isNewVersion, fileId: $fileId, id: $id) {
      ...AssetFields
    }
  }

  ${ASSET_FRAGMENT}
`;

export const ADD_DOWNLOAD_ATTACHMENT = gql`
  mutation AddDownloadAttachment(
    $label: String = "download"
    $extraTags: [String!] = []
    $fileId: String!
    $assetId: String!
  ) {
    addDownloadAttachment(
      label: $label
      extraTags: $extraTags
      fileId: $fileId
      assetId: $assetId
    ) {
      ...AssetFields
    }
  }

  ${ASSET_FRAGMENT}
`;

export const UPDATE_DOWNLOAD_ATTACHMENT_FILE = gql`
  mutation UpdateDownloadAttachmentFile(
    $isNewVersion: Boolean
    $fileId: String!
    $attachmentId: String!
    $assetId: String!
  ) {
    updateDownloadAttachmentFile(
      isNewVersion: $isNewVersion
      fileId: $fileId
      attachmentId: $attachmentId
      assetId: $assetId
    ) {
      ...AssetFields
    }
  }

  ${ASSET_FRAGMENT}
`;

export const UPDATE_ATTACHMENT = gql`
  mutation UpdateAttachment(
    $update: AttachmentInput!
    $attachmentId: String!
    $assetId: String!
  ) {
    updateAttachment(
      update: $update
      attachmentId: $attachmentId
      assetId: $assetId
    ) {
      ...AssetFields
    }
  }

  ${ASSET_FRAGMENT}
`;

export const ORDER_ATTACHMENT = gql`
  mutation OrderAttachments($attachmentIds: [String!]!, $assetId: String!) {
    orderAttachments(attachmentIds: $attachmentIds, assetId: $assetId) {
      ...AssetFields
    }
  }

  ${ASSET_FRAGMENT}
`;

export const DELETE_ATTACHMENT = gql`
  mutation DeleteAttachment($attachmentId: String!, $assetId: String!) {
    deleteAttachment(attachmentId: $attachmentId, assetId: $assetId) {
      ...AssetFields
    }
  }

  ${ASSET_FRAGMENT}
`;

export const REPLACE_ATTACHMENT = gql`
  mutation ReplaceAttachment(
    $attachmentId: String!
    $assetId: String!
    $label: String
    $extraTags: [String!]
    $fileId: String!
  ) {
    deleteAttachment(attachmentId: $attachmentId, assetId: $assetId) {
      ...AssetFields
    }

    addPreviewAttachment(
      label: $label
      extraTags: $extraTags
      fileId: $fileId
      assetId: $assetId
    ) {
      ...AssetFields
    }
  }

  ${ASSET_FRAGMENT}
`;

export const ADD_BACKPLATE = gql`
  mutation AddBackplate(
    $label: String
    $extraTags: [String!]
    $fileId: String!
    $assetId: String!
  ) {
    addBackplate(
      label: $label
      extraTags: $extraTags
      fileId: $fileId
      assetId: $assetId
    ) {
      ...AssetFields
    }
  }

  ${ASSET_FRAGMENT}
`;

export const DELETE_BACKPLATE = gql`
  mutation DeleteBackplate($downloadId: String!, $assetId: String!) {
    deleteBackplate(downloadId: $downloadId, assetId: $assetId) {
      ...AssetFields
    }
  }

  ${ASSET_FRAGMENT}
`;

export const REPLACE_BACKPLATE = gql`
  mutation ReplaceBackplate(
    $downloadId: String!
    $assetId: String!
    $label: String
    $extraTags: [String!]
    $fileId: String!
  ) {
    deleteBackplate(downloadId: $downloadId, assetId: $assetId) {
      ...AssetFields
    }

    addBackplate(
      label: $label
      extraTags: $extraTags
      fileId: $fileId
      assetId: $assetId
    ) {
      ...AssetFields
    }
  }

  ${ASSET_FRAGMENT}
`;
